import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataProvider } from 'react-admin';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import {
  ApiErrorResponse,
  BinaryUploadResponse,
  BloxCustomDataProviderType,
} from 'utility/types/dataProvider';
import { commonPreviewPayload } from 'providers/contexts/extensions/functions/formatters';
import { getBaseCleanUrl } from 'components/ImageUpload/ImageData';
import { AxiosProgressEvent } from 'axios';

/**
 * Formatted image data extracted from upload response.
 */
export type ImageUploadData = {
  binaryId: string;
  previewUrl?: string;
  cleanPreviewUrl?: string;
  width?: number;
  height?: number;
  type?: string;
  size?: number;
  fileName?: string;
};

/**
 * Hook arguments for useImageDynamicField
 */
export type TUseImageDynamicFieldArgs = {
  setLoadingStatus?: (status: string | null) => void;
};

/**
 * Hook return type for useImageDynamicField
 */
export type TUseImageDynamicFieldReturn = {
  mutation: UseMutationResult<BinaryUploadResponse, ApiErrorResponse, File, unknown>;
  isUploadingRef: React.MutableRefObject<boolean>;
  formatBinaryUploadResponse: (response: BinaryUploadResponse) => ImageUploadData | null;
  extractBinaryId: (value: any) => string | undefined;
  generatePreviewUrl: (binaryId: string | undefined) => string | undefined;
};

/**
 * Format binary upload response into ImageUploadData structure
 * Extracts and normalizes data from the API response
 */
export function formatBinaryUploadResponse(
  response: BinaryUploadResponse,
): ImageUploadData | null {
  if (!response?.data?.id) {
    return null;
  }

  const data = response.data;
  const previewUrl =
    data.previews?.preview?.url ||
    data.previews?.display?.url ||
    data.previews?.size1?.url ||
    data.previews?.size2?.url;

  const cleanPreviewUrl = getBaseCleanUrl(previewUrl);

  return {
    binaryId: data.id ?? '',
    previewUrl: previewUrl,
    cleanPreviewUrl: cleanPreviewUrl,
    width: data.width,
    height: data.height,
    type: data.type,
    size: data.size,
    fileName: data.clientName,
  };
}

/**
 * Extract binary ID from field value
 * Supports multiple formats:
 * - String (direct binary ID - legacy)
 * - Object with id property (standard format)
 */
export function extractBinaryId(value: any): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value; // Legacy support
  if (typeof value === 'object' && value !== null && 'id' in value) {
    return value.id;
  }
  return undefined;
}

/**
 * Generate preview URL from binary ID
 */
export function generatePreviewUrl(binaryId: string | undefined): string | undefined {
  if (!binaryId) return undefined;
  return `/core/binary/${binaryId}/preview`;
}

/**
 * Custom hook for image dynamic field functionality
 * Provides upload mutation, progress tracking, and helper utilities
 *
 * @param args - Optional configuration for loading status callback
 * @returns Object containing mutation, helpers, and utilities
 *
 * @example
 * ```tsx
 * const {
 *   mutation,
 *   isUploadingRef,
 *   formatBinaryUploadResponse,
 *   extractBinaryId,
 *   generatePreviewUrl,
 * } = useImageDynamicField({
 *   setLoadingStatus: (status) => setUploadStatus(status || ''),
 * });
 * ```
 */
export function useImageDynamicField({
  setLoadingStatus,
}: TUseImageDynamicFieldArgs = {}): TUseImageDynamicFieldReturn {
  const { t } = useTranslation();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const strUploading = t('blox.actions.uploading');

  // Ref to track upload state synchronously
  const isUploadingRef = useRef(false);

  const handleUploadProgress = useCallback(
    (progressEvent: AxiosProgressEvent) => {
      if (progressEvent?.loaded && progressEvent?.total) {
        const percentComplete = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100,
        );
        setLoadingStatus?.(`${strUploading}... ${percentComplete}%`);
      }
    },
    [setLoadingStatus, strUploading],
  );

  // Upload the file and extract metadata from api response
  const mutation = useMutation<BinaryUploadResponse, ApiErrorResponse, File>({
    mutationKey: ['useImageDynamicField', 'binaryUpload', '/core/upload/'],
    mutationFn: async (file: File) => {
      const binaryUpload = await dataProvider.binaryUpload({
        file: file,
        preview: commonPreviewPayload?.preview,
        onUploadProgress: handleUploadProgress,
      });
      return binaryUpload;
    },

    onMutate: (file) => {
      // set ref and status - called before mutation starts
      isUploadingRef.current = true;
      setLoadingStatus?.(`${strUploading}...`);
    },
    onSuccess: (data, file) => {
      // reset ref and status - mutation is done
      isUploadingRef.current = false;
      setLoadingStatus?.(null);
    },
    onError: (error, file) => {
      // reset ref and status - mutation is done
      isUploadingRef.current = false;
      setLoadingStatus?.(null);
      console.error('[useImageDynamicField] onError:', error);
    },
  });

  return {
    mutation,
    isUploadingRef,
    formatBinaryUploadResponse,
    extractBinaryId,
    generatePreviewUrl,
  };
}
